import * as React from "react";
import { useEffect, useReducer, useRef, useState } from 'react';
import { Spinner, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem, Button } from 'reactstrap';
import InfiniteScroll from 'react-infinite-scroller';
import * as Models from '../models';
import { SiteSearchService, SearchIn } from '../services/SiteSearchService';
import { bindActionCreators } from 'redux';
import classnames from 'classnames';
import { Facet } from "../models/PagedResult";
import { connect } from 'react-redux';
import * as SiteSearchStore from '../store/SiteSearchStore';
import { ApplicationState } from "../store";
import EmptySpace from './EmptySpace';

const PAGE_SIZE = 10;

type Props = {
    siteSections: Models.LookupItem[];
    searchStoreActions: typeof SiteSearchStore.actionCreators;
    searchStoreState: SiteSearchStore.IState;
    culture: string;
}
type SiteSearchPageState = {
    keywords: string;
    siteSections: Models.LookupItem[];
    siteSection: Models.LookupItem;
    canLoadMore: boolean;
    isLoading: boolean;
    isNoResults: boolean;
    items: Models.SiteSearchResultItem[];
    totalItems: number;
    lastFilter: SearchIn
    searchTrigger: number;
    facets: Facet[];
}

const initialState: SiteSearchPageState = {
    keywords: '',
    siteSections: [],
    siteSection: undefined,
    canLoadMore: false,
    isLoading: false,
    isNoResults: false,
    items: [],
    totalItems: 0,
    lastFilter: undefined,
    searchTrigger: 0,
    facets: []
}

const getUrlVars = (paramName) => {

    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.get(paramName);
}


const SiteSearchPage = (props: Props) => {

    const getInitialState = () => {
        //var defaultSearchType = searchTypes.find((x) => x.value == (props.deafultSearchType || ''));

        let result = initialState;
        result.siteSection = props.siteSections.length > 0 ? props.siteSections[0] : undefined;

        //if (defaultSearchType) {
        //    result.searchBy = defaultSearchType;
        //}

        result.keywords = getUrlVars('q') || '';

        return result;
    };

    const [state, setState] = useReducer(
        (prevState: SiteSearchPageState, newState: Partial<SiteSearchPageState>) => ({ ...prevState, ...newState }),
        getInitialState()
    );

    const search = async () => {

        if (state.keywords.length == 0) {
            window.history.pushState({}, '', '');
            setState({
                items: [],
                facets: [],
                isNoResults: false,
                canLoadMore: false,
                lastFilter: undefined,
                totalItems: 0,
                isLoading: false
            })
            return;
        }

        window.history.pushState({}, '', '?q=' + state.keywords);
        const lastFilter: SearchIn = {
            keywords: state.keywords,
            siteSection: state.siteSection ? state.siteSection.value : '',
            siteSectionTitle: state.siteSection ? state.siteSection.title : '',
            culture: props.culture
        };

        setState({
            isLoading: true,
            lastFilter: lastFilter,
            //items: [],
            //lastSearchBy: state.searchBy
        });

        const result = await SiteSearchService.search(lastFilter, PAGE_SIZE, 0);

        if (!result.hasErrors) {
            setState({
                items: result.value.items,
                totalItems: result.value.totalItems,
                isLoading: false,
                isNoResults: result.value.totalItems == 0,
                canLoadMore: result.value.totalItems > result.value.items.length,
                facets: result.value.facets
            })
        }
    }

    const loadMore = async () => {

        setState({
            isLoading: true
        });

        const result = await SiteSearchService.search(state.lastFilter, PAGE_SIZE, state.items.length);

        if (!result.hasErrors) {

            const newItemsSet = state.items.concat(result.value.items);

            setState({
                items: newItemsSet,
                totalItems: result.value.totalItems,
                isLoading: false,
                canLoadMore: result.value.totalItems > newItemsSet.length
            })
        }
    }

    const getFacetsCount = (sectionId: string) => {

        if (!state.lastFilter || state.lastFilter.keywords.length == 0 || state.facets.length == 0)
            return null;

        if (sectionId == '00000000-0000-0000-0000-000000000000') // all site
        {
            var totalItems = state.facets.reduce((prev, curr) => { return prev + curr.count; }, 0);

            return <>({totalItems})</>;
        }

        var facet = state.facets.find(x => x.value == sectionId);

        if (facet)
            return <>({facet.count})</>;

        return null;
    }

    const hasValues = (sectionId: string) => {

        if (!state.isNoResults && state.totalItems == 0)
            return true;

        if (sectionId.length == 0) // all site
            return true;

        var facet = state.facets.find(x => x.value == sectionId);

        if (facet)
            return true;

        return false;
    }

    const clearSearch = () => {
        setState({
            keywords: '',
            searchTrigger: state.searchTrigger + 1
        });
    }


    const highlightKeywords = (text: string, highlightKeywords: string) => {

        const words = (highlightKeywords || '').trim().split(' ');

        let result = text || '';

        words.forEach(word => {

            if (word.trim().length > 0) {

                const searchRegex = new RegExp('\\b' + word + '\\b', 'gi');
                result = result.replace(searchRegex, '<b>' + word + '</b>');
            }

        });

        return result;
    }


    useEffect(() => {

        search();

    }, [state.siteSection, state.searchTrigger]);



    useEffect(() => {

        if (props.searchStoreState.searchKeywords.length > 0) {
            setState({
                keywords: props.searchStoreState.searchKeywords,
                searchTrigger: state.searchTrigger + 1
            })
        } 

    }, [props.searchStoreState.searchKeywords]);

    useEffect(() => {

        if (state.keywords.length > 0)
            search();

    }, []);

    return (
        <>
            <div className="bg-dark">
                <div className="container">
                    <div className="row">
                        <div className="col">
                            <form className="site-search-filters">                                
                                    <div className="form-group">
                                        <label>Search</label>
                                        <div className="input-group mb-3">
                                            <input type="text" onChange={(e) => { setState({ keywords: e.target.value }) }}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        search();
                                                    }
                                            }}
                                            value={state.keywords}
                                            placeholder="Keywords Search"
                                            className="form-control flex-grow-1" />
                                        <button type="button" className={classnames({
                                            "btn btn-link bg-white flex-shrink-1": true,
                                            "d-none": state.keywords.length == 0
                                        })} onClick={clearSearch}><i className="fas fa-times"></i></button>
                                            <button type="button" className="btn btn-primary flex-shrink-1" onClick={search}>Search</button>
                                        </div>
                                    </div>
                                
                            </form>
                        </div>
                    </div>
                </div>
            </div>


            <div className="border-bottom mb-3">
                <div className="container">
                    <div className="row">
                        <div className="col">
                            <ul className="list-unstyled site-search-filters-site-sections d-flex flex-wrap my-0">
                                {props.siteSections.map(x => <li className={classnames({
                                    'nav-item': true,
                                    'active': state.siteSection && state.siteSection.value == x.value,
                                    'd-none': !hasValues(x.value)
                                })} key={x.title}>
                                    <a className="nav-link"
                                        href="#"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setState({ siteSection: x })
                                        }}
                                    >{x.title} {getFacetsCount(x.value)}
                                    </a>
                                </li>)}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            <div className="container">
                {!state.isNoResults && state.lastFilter &&
                    <div className="row">
                        <div className="col">
                            <h3 dangerouslySetInnerHTML={{ __html: state.lastFilter.siteSectionTitle + " (" + state.totalItems + ")" }}></h3>
                        </div>
                    </div>}

                {state.isNoResults &&
                    <div className="row" >
                        <div className="col">
                        <h3>No Results</h3>
                        <EmptySpace extraOffset={45}/>
                        </div>
                    </div>
                }

                {state.lastFilter && state.lastFilter.keywords.length == 0 && !state.isNoResults &&
                    <div className="row">
                        <div className="col">
                        <h3>Please enter keywords to search</h3>
                        <EmptySpace extraOffset={45} />
                        </div>
                    </div>                    
                }                

                <div className="row">
                    <InfiniteScroll
                        pageStart={0}
                        loadMore={loadMore}
                        hasMore={state.canLoadMore && !state.isLoading && state.lastFilter}
                        useWindow={true}
                        className="col"
                    >
                        {state.items.map(item => {
                            return <div className="site-search-search-result-item border-bottom py-3" key={item.nodeId} >
                                <div className="d-flex justify-content-between results-item-title">
                                    <a href={item.url}>
                                        <h6 dangerouslySetInnerHTML={{ __html: highlightKeywords(item.title, state.lastFilter.keywords)}}></h6>
                                    </a>
                                    <div className="category text-uppercase" dangerouslySetInnerHTML={{ __html: item.siteSectionTitle }}></div>
                                </div>
                                <div className="description" dangerouslySetInnerHTML={{ __html: highlightKeywords(item.description, state.lastFilter.keywords) }}>
                                </div>
                                <a className="link-path" href={item.url}>
                                    {item.url.substring(window.location.origin.length)}
                                </a>
                            </div>
                        })}
                    </InfiniteScroll>
                </div>
                <div className="text-center" style={{
                    marginTop: "-12px",
                    height: "45px"
                }}>
                    {state.isLoading &&
                        <Spinner color="primary" />
                    }
                </div>
            </div>

        </>
    )
}

export default connect(
    (state: ApplicationState) => ({
        searchStoreState: state.siteSearchStore
    }), // Selects which state properties are merged into the component's props
    (dispatch, getState) => ({
        searchStoreActions: bindActionCreators(SiteSearchStore.actionCreators, dispatch)
    })

)(SiteSearchPage as any);